/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ConfiguratorDto } from '../models/ConfiguratorDto';
import type { ConfiguratorLayout } from '../models/ConfiguratorLayout';
import type { PriceBreakdownDto } from '../models/PriceBreakdownDto';
import type { ProductionSpeedType } from '../models/ProductionSpeedType';
import type { QuantityDimensions } from '../models/QuantityDimensions';
import type { QuantityStepBaseDto } from '../models/QuantityStepBaseDto';
import type { SelectMode } from '../models/SelectMode';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ConfiguratorService {

    /**
     * @returns ConfiguratorDto Returns steps for the configurator
     * @throws ApiError
     */
    public static getGetConfigurator({
        code,
        locale,
        colors,
        colorSelectMode,
        country,
        debtor,
        versions,
        printOptions,
        printOptionFilter,
        quantity,
        quantities,
        quantityDimension,
        configurationId,
        layout,
        unprinted,
        productionSpeed,
    }: {
        /**
         * Product code
         */
        code: string,
        /**
         * Locale
         */
        locale: string,
        /**
         * Colors
         */
        colors?: Array<string>,
        /**
         * Color select mode.
         */
        colorSelectMode?: SelectMode,
        /**
         * Country code
         */
        country?: string,
        /**
         * Debtor code
         */
        debtor?: string,
        /**
         * Versions
         */
        versions?: Array<string>,
        /**
         * Print options
         */
        printOptions?: Record<string, {
            colorCount?: number;
        }>,
        /**
         * Print option filters
         */
        printOptionFilter?: Record<string, string>,
        /**
         * Quantity
         */
        quantity?: number,
        /**
         * Quantities by size and/or color
         */
        quantities?: Record<string, (number | Record<string, number>)>,
        /**
         * Dimensions of quantities parameter
         */
        quantityDimension?: QuantityDimensions,
        /**
         * An existing configuration UUID.
         */
        configurationId?: string,
        /**
         * Layout of the configurator.
         */
        layout?: ConfiguratorLayout,
        /**
         * Whether unprinted is selected.
         */
        unprinted?: 0 | 1,
        /**
         * Production speed parameter
         */
        productionSpeed?: ProductionSpeedType,
    }): CancelablePromise<ConfiguratorDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{code}/configurator',
            path: {
                'code': code,
            },
            query: {
                'locale': locale,
                'colors[]': colors,
                'colorSelectMode': colorSelectMode,
                'country': country,
                'debtor': debtor,
                'versions[]': versions,
                'printOptions': printOptions,
                'printOptionFilter': printOptionFilter,
                'quantity': quantity,
                'quantities': quantities,
                'quantityDimension': quantityDimension,
                'configurationId': configurationId,
                'layout': layout,
                'unprinted': unprinted,
                'productionSpeed': productionSpeed,
            },
            errors: {
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns PriceBreakdownDto Returns price breakdown for the configurator
     * @throws ApiError
     */
    public static getGetConfiguratorPriceBreakdown({
        code,
        locale,
        vatRate,
        colors,
        colorSelectMode,
        country,
        debtor,
        versions,
        printOptions,
        printOptionFilter,
        quantity,
        quantities,
        quantityDimension,
        configurationId,
        layout,
        unprinted,
        productionSpeed,
    }: {
        /**
         * Product code
         */
        code: string,
        /**
         * Locale
         */
        locale: string,
        /**
         * VAT rate
         */
        vatRate?: string,
        /**
         * Colors
         */
        colors?: Array<string>,
        /**
         * Color select mode.
         */
        colorSelectMode?: SelectMode,
        /**
         * Country code
         */
        country?: string,
        /**
         * Debtor code
         */
        debtor?: string,
        /**
         * Versions
         */
        versions?: Array<string>,
        /**
         * Print options
         */
        printOptions?: Record<string, {
            colorCount?: number;
        }>,
        /**
         * Print option filters
         */
        printOptionFilter?: Record<string, string>,
        /**
         * Quantity
         */
        quantity?: number,
        /**
         * Quantities by size and/or color
         */
        quantities?: Record<string, (number | Record<string, number>)>,
        /**
         * Dimensions of quantities parameter
         */
        quantityDimension?: QuantityDimensions,
        /**
         * An existing configuration UUID.
         */
        configurationId?: string,
        /**
         * Layout of the configurator.
         */
        layout?: ConfiguratorLayout,
        /**
         * Whether unprinted is selected.
         */
        unprinted?: 0 | 1,
        /**
         * Production speed parameter
         */
        productionSpeed?: ProductionSpeedType,
    }): CancelablePromise<PriceBreakdownDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{code}/configurator/price-breakdown',
            path: {
                'code': code,
            },
            query: {
                'locale': locale,
                'vatRate': vatRate,
                'colors[]': colors,
                'colorSelectMode': colorSelectMode,
                'country': country,
                'debtor': debtor,
                'versions[]': versions,
                'printOptions': printOptions,
                'printOptionFilter': printOptionFilter,
                'quantity': quantity,
                'quantities': quantities,
                'quantityDimension': quantityDimension,
                'configurationId': configurationId,
                'layout': layout,
                'unprinted': unprinted,
                'productionSpeed': productionSpeed,
            },
            errors: {
                401: `Unauthorized`,
                403: `Access denied.`,
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns QuantityStepBaseDto Returns quantity step for the configurator
     * @throws ApiError
     */
    public static getGetConfiguratorQuantityStep({
        code,
        locale,
        colors,
        colorSelectMode,
        country,
        debtor,
        versions,
        printOptions,
        printOptionFilter,
        quantity,
        quantities,
        quantityDimension,
        configurationId,
        layout,
        unprinted,
        productionSpeed,
    }: {
        /**
         * Product code
         */
        code: string,
        /**
         * Locale
         */
        locale: string,
        /**
         * Colors
         */
        colors?: Array<string>,
        /**
         * Color select mode.
         */
        colorSelectMode?: SelectMode,
        /**
         * Country code
         */
        country?: string,
        /**
         * Debtor code
         */
        debtor?: string,
        /**
         * Versions
         */
        versions?: Array<string>,
        /**
         * Print options
         */
        printOptions?: Record<string, {
            colorCount?: number;
        }>,
        /**
         * Print option filters
         */
        printOptionFilter?: Record<string, string>,
        /**
         * Quantity
         */
        quantity?: number,
        /**
         * Quantities by size and/or color
         */
        quantities?: Record<string, (number | Record<string, number>)>,
        /**
         * Dimensions of quantities parameter
         */
        quantityDimension?: QuantityDimensions,
        /**
         * An existing configuration UUID.
         */
        configurationId?: string,
        /**
         * Layout of the configurator.
         */
        layout?: ConfiguratorLayout,
        /**
         * Whether unprinted is selected.
         */
        unprinted?: 0 | 1,
        /**
         * Production speed parameter
         */
        productionSpeed?: ProductionSpeedType,
    }): CancelablePromise<QuantityStepBaseDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{code}/configurator/steps/quantity',
            path: {
                'code': code,
            },
            query: {
                'locale': locale,
                'colors[]': colors,
                'colorSelectMode': colorSelectMode,
                'country': country,
                'debtor': debtor,
                'versions[]': versions,
                'printOptions': printOptions,
                'printOptionFilter': printOptionFilter,
                'quantity': quantity,
                'quantities': quantities,
                'quantityDimension': quantityDimension,
                'configurationId': configurationId,
                'layout': layout,
                'unprinted': unprinted,
                'productionSpeed': productionSpeed,
            },
            errors: {
                401: `Unauthorized`,
                403: `Access denied.`,
                404: `Product not found.`,
            },
        });
    }

}
